import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x7_multifunktionsrelais';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface FunktionInfo {
  name: string;
  titel: string;
  beschreibungDetail: string;
  anwendungsbeispiele: string;
  anschlussHinweis: string;
  beispielRelaisInfo: string;
}

const funktionenListe: FunktionInfo[] = [
  {
    name: 'AV – Ansprechverzögerung',
    titel: 'Funktion: Ansprechverzögerung (AV / On-Delay)',
    beschreibungDetail: 'Nach Anlegen der Betriebsspannung oder eines Steuersignals läuft die eingestellte Zeit t. Erst danach zieht das Relais an. Wird die Spannung/der Trigger vor Ablauf entfernt, wird zurückgesetzt.',
    anwendungsbeispiele: 'Sanftes Zuschalten von Lasten; zeitverzögerte Einschaltung von Lüftern/Pumpen; Entprellung.',
    anschlussHinweis: 'Spulenseitig A1/A2; Trigger-Eingang je nach Gerät separat. Zeit über Drehschalter/Tasten einstellbar; Kontaktfunktion COM/NO/NC gemäß Schaltplan.',
    beispielRelaisInfo: 'Eltako MFZ12; ABB CT-MFD – AV-Funktion über Funktionswahlschalter, Klemmen A1/A2, Relaiskontakte 15/16/18.'
  },
  {
    name: 'RV – Abfallverzögerung',
    titel: 'Funktion: Abfallverzögerung (RV / Off-Delay)',
    beschreibungDetail: 'Relais zieht sofort an und fällt erst nach Ablauf der eingestellten Zeit ab, wenn das Steuersignal wegfällt.',
    anwendungsbeispiele: 'Nachlaufsteuerungen (z. B. Lüfternachlauf im Sanitärbereich), Haltezeiten.',
    anschlussHinweis: 'A1/A2 Spule; je nach Gerät Steuer-/Triggerklemmen separat. Zeitparameter am Gerät; Kontaktbezeichnungen gemäß Hersteller.',
    beispielRelaisInfo: 'ABB CT-MFD – RV; Eltako MFZ12 – RV; Verdrahtung gemäß Datenblatt.'
  },
  {
    name: 'IP – Impulsrelais / Takt',
    titel: 'Funktion: Impuls/Takt (Periodisch)',
    beschreibungDetail: 'Erzeugt periodische Ein-/Aus-Zyklen (Taktfunktion). Ein und Aus Zeiten einstellbar, Relais wechselt entsprechend.',
    anwendungsbeispiele: 'Blink-/Taktsteuerungen, zyklische Ansteuerung, Testsignale.',
    anschlussHinweis: 'A1/A2 Spule; ggf. Start/Reset. Einstellungen über Potis/Drehschalter. Kontakte potentialfrei gemäß Gerät.',
    beispielRelaisInfo: 'ABB CT-MFD – Takt (CYCLIC); Eltako MFZ12 – IP/TA.'
  }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedFunktion, setSelectedFunktion] = React.useState<string>(funktionenListe[0].name);
  const current = funktionenListe.find(f => f.name === selectedFunktion) || funktionenListe[0];

  const introBlocks = React.useMemo(() => [
    { heading: 'Multifunktionsrelais: Überblick', body: 'Multifunktionsrelais vereinen verschiedene Zeit- und Logikfunktionen (AV, RV, Takt etc.) in einem Gerät. Parametrierung erfolgt meist direkt am Gerät über Drehschalter/Tasten.' },
    { heading: 'Einsatz und Parametrierung', body: 'Typische Anwendungen: Nachlauf, verzögertes Einschalten, Taktbetrieb. Parameter: Zeit, Funktion, Kontaktart. Korrekte Verdrahtung von Spule (A1/A2) und Kontaktseite (COM/NO/NC) beachten.' }
  ], []);

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override, introBlocks]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: current.titel, body: override?.texts?.[`dyn_${selectedFunktion}_0`] || current.beschreibungDetail },
    { heading: 'Anwendungsbeispiele', body: override?.texts?.[`dyn_${selectedFunktion}_1`] || current.anwendungsbeispiele },
    { heading: 'Anschlusshinweise', body: override?.texts?.[`dyn_${selectedFunktion}_2`] || current.anschlussHinweis },
    { heading: 'Beispiel-Relais', body: override?.texts?.[`dyn_${selectedFunktion}_3`] || current.beispielRelaisInfo }
  ], [selectedFunktion, override, current]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() =>
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '6.7.7 Multifunktionsrelais', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Multifunktionsrelais',
            label: 'MFR-Bild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, { key: 0, pageId: PAGE_ID, blockId: 'intro_0', heading: mergedIntroBlocks[0].heading, body: mergedIntroBlocks[0].body, onSave: (newBody: string) => handleTextSave('intro_0', newBody) }),
        React.createElement(EditableTextBlock, { key: 1, pageId: PAGE_ID, blockId: 'intro_1', heading: mergedIntroBlocks[1].heading, body: mergedIntroBlocks[1].body, onSave: (newBody: string) => handleTextSave('intro_1', newBody) }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'mfr-select-label' }, 'Funktion auswählen'),
          React.createElement(Select, {
            labelId: 'mfr-select-label',
            id: 'mfr-select',
            value: selectedFunktion,
            label: 'Funktion auswählen',
            onChange: (e: any) => setSelectedFunktion(String(e.target.value))
          },
            ...funktionenListe.map((f) =>
              React.createElement(MenuItem, { key: f.name, value: f.name }, f.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedFunktion, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedFunktion}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, { key: `${selectedFunktion}_0`, pageId: PAGE_ID, blockId: `dyn_${selectedFunktion}_0`, heading: mergedDynamicBlocks[0]?.heading || 'Allgemeine Beschreibung', body: mergedDynamicBlocks[0]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedFunktion}_0`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedFunktion}_1`, pageId: PAGE_ID, blockId: `dyn_${selectedFunktion}_1`, heading: 'Anwendungsbeispiele', body: mergedDynamicBlocks[1]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedFunktion}_1`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedFunktion}_2`, pageId: PAGE_ID, blockId: `dyn_${selectedFunktion}_2`, heading: 'Anschlusshinweise', body: mergedDynamicBlocks[2]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedFunktion}_2`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedFunktion}_3`, pageId: PAGE_ID, blockId: `dyn_${selectedFunktion}_3`, heading: 'Beispiel-Relais', body: mergedDynamicBlocks[3]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedFunktion}_3`, newBody) }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u7_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


